/**
 * Filters for sport clubs.
 */
import * as React from 'react';
import { Select } from '@/components/forms/Select';
import { SearchInput } from '@/components/forms/SearchInput';
import {
  SPORT_CLUB_FILTER_IDS,
  SPORT_TYPE_FILTER_OPTIONS,
  type SportClubFilters,
} from './sport-club-configs';

/** Props for {@link SportClubFiltersContent}. */
interface SportClubFiltersContentProps {
  filters: SportClubFilters;
  allowedFilterIds: string[];
  visibleFilterIds: string[];
  onSetSearch: (v: string | undefined) => void;
  onSetCity: (v: string | undefined) => void;
  onSetSportType: (v: string | undefined) => void;
}

/** Filter group is allowed by RBAC and currently toggled on in the configure panel. */
function isVisible(filterId: string, allowed: string[], visible: string[]): boolean {
  return allowed.includes(filterId) && visible.includes(filterId);
}

/** Free-text search across sport-club fields. */
const SearchFilter: React.FC<{
  filters: SportClubFilters;
  onSetSearch: (v: string | undefined) => void;
}> = ({ filters, onSetSearch }) => (
  <div className="filter-group">
    <span className="filter-group-label">Recherche</span>
    <div className="w-56">
      <SearchInput
        size="sm"
        shortcut={false}
        debounceMs={300}
        placeholder="Rechercher un club..."
        value={filters.search ?? ''}
        onSearch={(q) => onSetSearch(q.trim() || undefined)}
        onClear={() => onSetSearch(undefined)}
      />
    </div>
  </div>
);

/** Free-text city filter. */
const CityFilter: React.FC<{
  filters: SportClubFilters;
  onSetCity: (v: string | undefined) => void;
}> = ({ filters, onSetCity }) => (
  <div className="filter-group">
    <span className="filter-group-label">Ville</span>
    <div className="w-48">
      <SearchInput
        size="sm"
        shortcut={false}
        debounceMs={300}
        placeholder="Filtrer par ville..."
        value={filters.city ?? ''}
        onSearch={(q) => onSetCity(q.trim() || undefined)}
        onClear={() => onSetCity(undefined)}
      />
    </div>
  </div>
);

/** Single-select sport type. */
const SportTypeFilter: React.FC<{
  filters: SportClubFilters;
  onSetSportType: (v: string | undefined) => void;
}> = ({ filters, onSetSportType }) => (
  <div className="filter-group">
    <span className="filter-group-label">Sport</span>
    <Select
      options={SPORT_TYPE_FILTER_OPTIONS}
      value={filters.sportType ?? ''}
      onChange={(val) => onSetSportType(val || undefined)}
      size="sm"
      className="min-w-48"
    />
  </div>
);

/** Ribbon of sport-club filters; each group is gated by RBAC + configure-panel visibility. */
export const SportClubFiltersContent: React.FC<SportClubFiltersContentProps> = ({
  filters,
  allowedFilterIds,
  visibleFilterIds,
  onSetSearch,
  onSetCity,
  onSetSportType,
}) => {
  const show = (id: string) => isVisible(id, allowedFilterIds, visibleFilterIds);

  return (
    <div className="filter-groups-container">
      {show(SPORT_CLUB_FILTER_IDS.search) && <SearchFilter filters={filters} onSetSearch={onSetSearch} />}
      {show(SPORT_CLUB_FILTER_IDS.city) && <CityFilter filters={filters} onSetCity={onSetCity} />}
      {show(SPORT_CLUB_FILTER_IDS.sportType) && (
        <SportTypeFilter filters={filters} onSetSportType={onSetSportType} />
      )}
    </div>
  );
};
